import { FC, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useGetBroadcastMessagesHook } from '../../hooks/useMessagingHook';
import { Message } from '../../../../../apis/messaging/messaging.interfaces';

function formatTimeAgo(dateStr: string | Date): string {
  const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

const AnnouncementsView: FC = () => {
  const { translateText } = useTranslation();
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Message | null>(null);

  const {
    data: broadcastData,
    isLoading,
    refetch,
  } = useGetBroadcastMessagesHook(undefined, { page: 1, limit: 50 });

  const broadcasts = broadcastData?.data || [];

  return (
    <Box
      sx={{
        height: 'calc(100vh - 150px)',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        bgcolor: 'grey.100',
        borderRadius: { xs: 0, sm: 1 },
        overflow: 'hidden',
        px: { xs: 0, sm: 1 },
        py: { xs: 1, sm: 1 },
      }}
      data-testid="announcements-view"
    >
      {/* Announcement List */}
      <Paper
        elevation={0}
        sx={{
          width: { xs: '100%', md: '40%' },
          minWidth: { xs: 'auto', md: 320 },
          maxWidth: { xs: '100%', md: 450 },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRight: { xs: 0, md: 1 },
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            {translateText('communications.announcements.title') || 'Announcements'}
          </Typography>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => refetch()}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : broadcasts.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CampaignIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
            <Typography color="text.secondary" variant="body2">
              No announcements yet
            </Typography>
          </Box>
        ) : (
          <List sx={{ flex: 1, overflow: 'auto', p: 0 }}>
            {broadcasts.map((broadcast, index) => {
              const msgId = broadcast._id || broadcast.id || String(index);
              const isSelected =
                selectedAnnouncement &&
                (selectedAnnouncement._id || selectedAnnouncement.id) === msgId;

              return (
                <Box key={msgId}>
                  <ListItem
                    onClick={() => setSelectedAnnouncement(broadcast)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: isSelected ? 'action.selected' : 'transparent',
                      '&:hover': { bgcolor: 'action.hover' },
                      py: 1.5,
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <CampaignIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          fontWeight={broadcast.is_read ? 400 : 600}
                          noWrap
                        >
                          {broadcast.content}
                        </Typography>
                      }
                      secondary={
                        <Box
                          component="span"
                          sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {broadcast.sender?.first_name
                              ? `${broadcast.sender.first_name} ${broadcast.sender.last_name}`
                              : 'Staff'}
                          </Typography>
                          <Typography variant="caption" color="text.disabled">
                            {formatTimeAgo(broadcast.created_at)}
                          </Typography>
                        </Box>
                      }
                    />
                    {broadcast.class_id && (
                      <Chip
                        label={broadcast.section || 'Class'}
                        size="small"
                        variant="outlined"
                        sx={{ ml: 1, fontSize: '0.7rem' }}
                      />
                    )}
                  </ListItem>
                  {index < broadcasts.length - 1 && <Divider component="li" />}
                </Box>
              );
            })}
          </List>
        )}
      </Paper>

      {/* Announcement Detail */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          p: { xs: 2, md: 3 },
          overflow: 'auto',
        }}
      >
        {selectedAnnouncement ? (
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 44, height: 44 }}>
                <CampaignIcon />
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  {selectedAnnouncement.sender?.first_name
                    ? `${selectedAnnouncement.sender.first_name} ${selectedAnnouncement.sender.last_name}`
                    : 'Staff'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(selectedAnnouncement.created_at).toLocaleString()}
                </Typography>
              </Box>
            </Box>

            {selectedAnnouncement.class_id && (
              <Chip
                label={
                  selectedAnnouncement.section
                    ? `Section ${selectedAnnouncement.section}`
                    : 'Class-wide'
                }
                size="small"
                color="primary"
                variant="outlined"
                sx={{ mb: 2 }}
              />
            )}

            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
              {selectedAnnouncement.content}
            </Typography>
          </Paper>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CampaignIcon sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
            <Typography color="text.secondary">
              Select an announcement to view details
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AnnouncementsView;
